// Plain-language labels + client-side matching logic for audiences.
// Single source of truth shared by the Audience Library and (phase 2) the
// per-experiment Audiences tab — replaces the operator label maps that were
// duplicated across components.
//
// Domain recap: an audience is a rule `attribute <operation> [values]`.
// Operations come from the backend AudienceOperator enum: IN, GT, GTE, LT, LTE, BTWN.

/** Sentence fragment for each operation, e.g. "app version <phrase> 12". */
export const OPERATION_PHRASE = {
    IN:   'is any of',
    GTE:  'is at least',
    GT:   'is greater than',
    LTE:  'is at most',
    LT:   'is less than',
    BTWN: 'is between',
};

/** Options for the builder's "How should it match?" select, in plain English. */
export const MATCH_TYPE_OPTIONS = [
    { value: 'IN',   label: 'is any of these values' },
    { value: 'GTE',  label: 'is at least (greater than or equal to)' },
    { value: 'GT',   label: 'is greater than' },
    { value: 'LTE',  label: 'is at most (less than or equal to)' },
    { value: 'LT',   label: 'is less than' },
    { value: 'BTWN', label: 'is between two values' },
];

/** Range operations only make sense for numeric / version attributes. */
export const RANGE_OPERATIONS = ['GT', 'GTE', 'LT', 'LTE', 'BTWN'];

/** Operations that compare against a single threshold value. */
const SINGLE_VALUE_OPS = ['GT', 'GTE', 'LT', 'LTE'];

const toValueStrings = (values) =>
    (values ?? []).map(v => (typeof v === 'string' ? v : v?.value)).filter(v => v != null && v !== '');

/**
 * Plain-English description of an audience rule.
 * e.g. "Matches users whose app version is at least 220509020"
 * Returns a string suitable for tooltips / previews / screen readers.
 */
export const describeAudience = ({ attributeName, operation, values }) => {
    const phrase = OPERATION_PHRASE[operation] ?? operation ?? '';
    const vals = toValueStrings(values);
    const subject = `Matches users whose ${attributeName ?? 'property'} ${phrase}`.trim();

    if (vals.length === 0) return `${subject} …`;
    if (operation === 'BTWN') {
        const [lo, hi] = [...vals].sort((a, b) => compareValues(a, b, true)).slice(0, 2);
        return hi != null ? `${subject} ${lo} and ${hi}` : `${subject} ${lo} …`;
    }
    if (SINGLE_VALUE_OPS.includes(operation)) return `${subject} ${vals[0]}`;
    // IN — list match
    return `${subject} ${vals.join(', ')}`;
};

/**
 * Returns a human warning string if the audience's value count is invalid for
 * its operation, else null. (0 values never matches; range ops need exactly 1;
 * BTWN needs exactly 2.)
 */
export const valueCountIssue = (operation, count) => {
    const n = count ?? 0;
    if (n === 0) return "No values set yet, so it won't match anyone";
    if (operation === 'BTWN' && n !== 2) return `"Between" needs exactly 2 values (has ${n})`;
    if (SINGLE_VALUE_OPS.includes(operation) && n > 1) return `This match type uses a single value (has ${n})`;
    return null;
};

/** Strict numeric parse: returns a finite number or null (rejects "200abc", "", "1.2.3"). */
const asNumber = (v) => {
    const n = Number(String(v).trim());
    return Number.isFinite(n) ? n : null;
};

/** Numeric-aware comparison; falls back to natural string ordering. */
export const compareValues = (a, b, isNumeric) => {
    if (isNumeric) {
        const na = asNumber(a);
        const nb = asNumber(b);
        if (na !== null && nb !== null) return na - nb;
    }
    return String(a).localeCompare(String(b), undefined, { numeric: true });
};

/**
 * Would a user with `userValue` match this audience? Pure client-side mirror of
 * the backend operator semantics — used by the "try it out" tester.
 * Returns true / false, or null when the rule can't be evaluated
 * (no values, or a range/between rule with the wrong number of values).
 */
export const audienceMatches = (operation, values, userValue, isNumeric) => {
    const vals = toValueStrings(values);
    const target = String(userValue ?? '').trim();
    if (target === '' || vals.length === 0) return null;

    if (operation === 'IN') {
        return vals.some(v => String(v).trim() === target);
    }
    // Range comparisons against a non-numeric user value can't be satisfied
    // (mirrors the backend, and avoids "v12" being read as ≥ 12).
    const numericMismatch = isNumeric && asNumber(target) === null;
    if (operation === 'BTWN') {
        if (vals.length !== 2) return null;
        if (numericMismatch) return false;
        const [lo, hi] = [...vals].sort((a, b) => compareValues(a, b, isNumeric));
        return compareValues(target, lo, isNumeric) >= 0 && compareValues(target, hi, isNumeric) <= 0;
    }
    if (SINGLE_VALUE_OPS.includes(operation)) {
        if (vals.length !== 1) return null; // ambiguous until the value count is correct
        if (numericMismatch) return false;
        const cmp = compareValues(target, vals[0], isNumeric);
        if (operation === 'GT')  return cmp > 0;
        if (operation === 'GTE') return cmp >= 0;
        if (operation === 'LT')  return cmp < 0;
        if (operation === 'LTE') return cmp <= 0;
    }
    return null;
};
